import React from 'react';
import { Link, useParams } from 'react-router-dom';
import SEO from '../components/SEO';
import Icon from '../components/Icon';
import Reveal from '../components/Reveal';
import PageHero from '../components/PageHero';
import CTA from '../components/CTA';
import NotFound from './NotFound';
import services, { getService } from '../data/services';
import './ServicePage.css';

const ServicePage = () => {
  const { slug } = useParams();
  const service = getService(slug);

  if (!service) return <NotFound />;

  const url = `https://escr.online/services/${service.slug}`;
  const structuredData = [
    {
      '@context': 'https://schema.org',
      '@type': 'Service',
      serviceType: service.serviceType,
      provider: {
        '@type': 'Organization',
        name: 'ESCR LLC',
        url: 'https://escr.online'
      },
      areaServed: 'Worldwide',
      description: service.schemaDescription,
      url,
      name: service.schemaName
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://escr.online' },
        { '@type': 'ListItem', position: 2, name: 'Services', item: 'https://escr.online/services' },
        { '@type': 'ListItem', position: 3, name: service.name, item: url }
      ]
    }
  ];

  const otherServices = services.filter((s) => s.slug !== service.slug);

  return (
    <div className="service-page">
      <SEO
        title={service.seo.title}
        description={service.seo.description}
        keywords={service.seo.keywords}
        structuredData={structuredData}
        canonicalUrl={url}
        ogType="website"
      />

      <PageHero eyebrow={`Services / ${service.name}`} title={service.heroTitle} subtitle={service.heroSubtitle}>
        <div className="btn-row service-hero-actions">
          <Link to="/contact" className="btn btn-primary btn-large">
            Get started
            <Icon name="arrowRight" />
          </Link>
          <a href="#capabilities" className="btn btn-secondary btn-large">
            Explore capabilities
          </a>
        </div>
      </PageHero>

      {/* Intro */}
      <section className="section section-tight">
        <div className="container service-intro">
          <Reveal className="service-intro-label">
            <span className="icon-tile service-intro-icon">
              <Icon name={service.icon} />
            </span>
            <h2 className="service-intro-title">{service.introTitle}</h2>
          </Reveal>
          <Reveal delay={100}>
            <p className="service-intro-text">{service.intro}</p>
          </Reveal>
        </div>
      </section>

      {/* Capabilities */}
      <section className="section section-tight" id="capabilities">
        <div className="container">
          <Reveal className="section-head">
            <span className="eyebrow">Capabilities</span>
            <h2 className="section-title">{service.featuresTitle}</h2>
          </Reveal>
          <div className="capabilities">
            {service.features.map((feature, i) => (
              <Reveal key={feature.title} delay={(i % 3) * 80} className="card capability">
                <span className="icon-tile">
                  <Icon name={feature.icon} />
                </span>
                <h3 className="capability-title">{feature.title}</h3>
                <p className="capability-desc">{feature.description}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Stack */}
      <section className="section section-tight">
        <div className="container">
          <Reveal className="section-head">
            <span className="eyebrow">Stack</span>
            <h2 className="section-title">{service.stackTitle}</h2>
          </Reveal>
          <div className="stack">
            {service.stack.map((group, i) => (
              <Reveal key={group.title} delay={i * 80} className="stack-col">
                <div className="stack-head">
                  <span className="stack-index">0{i + 1}</span>
                  <h3 className="stack-title">{group.title}</h3>
                </div>
                <ul className="stack-list">
                  {group.items.map((item) => (
                    <li key={item}>
                      <Icon name="check" />
                      {item}
                    </li>
                  ))}
                </ul>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Other services */}
      <section className="section section-tight">
        <div className="container">
          <Reveal className="section-head">
            <span className="eyebrow">More services</span>
            <h2 className="section-title">Everything else you'll need</h2>
          </Reveal>
          <div className="other-services">
            {otherServices.map((s, i) => (
              <Reveal key={s.slug} delay={i * 80}>
                <Link to={`/services/${s.slug}`} className="card other-service">
                  <span className="icon-tile">
                    <Icon name={s.icon} />
                  </span>
                  <span className="other-service-body">
                    <span className="other-service-name">{s.name}</span>
                    <span className="other-service-desc">{s.short}</span>
                  </span>
                  <Icon name="arrowUpRight" className="other-service-arrow" />
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTA
        title={service.cta.title}
        description={service.cta.description}
        primary={{ to: '/contact', label: 'Get started' }}
        secondary={{ to: '/about', label: 'About ESCR' }}
      />
    </div>
  );
};

export default ServicePage;
